import { createFileRoute } from "@tanstack/react-router"
import type { ReactNode } from "react"
import { ContactRow } from "@/components/ui/contact-row"
import { neonHeadingClass } from "@/components/ui/neon"
import { PageVignette } from "@/components/ui/page-vignette"
import { SectionHeader } from "@/components/ui/section-header"
import { StarField } from "@/components/ui/star-field"
import { cn } from "@/lib/utils"

export const Route = createFileRoute("/auteur")({
  head: () => ({
    meta: [
      { title: "Auteur | Pradeep Shahi — EKLO-Verse" },
      {
        name: "description",
        content:
          "Pradeep Shahi — director of EKLO. Filmography includes 1915: Legend of the Gurkhas, The Last Ritual, EKLO — I and EKLO · II.",
      },
      { property: "og:title", content: "Auteur | Pradeep Shahi" },
      {
        property: "og:description",
        content: "Pradeep Shahi — director of EKLO and the EKLO-Verse saga.",
      },
      {
        property: "og:image",
        content:
          "https://70fy8xbp6b.ufs.sh/f/BzM3qquY5hLZc4iA811CIC6mdFl0HiuWPaXK9wMYRAtVvNzJ",
      },
      {
        name: "twitter:image",
        content:
          "https://70fy8xbp6b.ufs.sh/f/BzM3qquY5hLZc4iA811CIC6mdFl0HiuWPaXK9wMYRAtVvNzJ",
      },
      { property: "og:url", content: "https://eklo-seven.vercel.app/auteur" },
    ],
    links: [{ rel: "canonical", href: "https://eklo-seven.vercel.app/auteur" }],
  }),
  component: AuteurPage,
})

const PORTRAIT_VIGNETTE =
  "linear-gradient(90deg,#03050a 0%,rgba(0,0,0,0.92) 14%,rgba(0,0,0,0.35) 38%,rgba(0,0,0,0.35) 62%,rgba(0,0,0,0.92) 86%,#03050a 100%),radial-gradient(ellipse at 65% 45%,transparent 28%,rgba(0,0,0,0.55) 88%),linear-gradient(180deg,transparent 0%,transparent 35%,rgba(0,0,0,0.6) 60%,#03050a 80%)"

function AuteurPage() {
  return (
    <div className="fade-in relative animate-in overflow-x-hidden duration-500">
      <StarField />
      <PageVignette />

      <section className="relative overflow-hidden md:min-h-[calc(100vh-80px)] md:py-20 md:pt-24">
        <div
          aria-hidden="true"
          className="pointer-events-none relative z-0 mt-16 h-[42vh] w-full overflow-hidden sm:mt-20 md:absolute md:top-0 md:right-0 md:bottom-0 md:mt-0 md:h-auto md:w-[62%]"
        >
          <img
            src="https://70fy8xbp6b.ufs.sh/f/BzM3qquY5hLZc4iA811CIC6mdFl0HiuWPaXK9wMYRAtVvNzJ"
            alt=""
            width={1600}
            height={2000}
            className="h-full w-full object-cover brightness-95 contrast-[1.05] grayscale-[0.1] [object-position:50%_18%] md:origin-top md:-translate-y-[14%] md:scale-75 md:[object-position:60%_0%]"
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2 bg-linear-to-b from-transparent to-black md:hidden"
          />
          <div
            className="absolute inset-0 hidden md:block"
            style={{ background: PORTRAIT_VIGNETTE }}
          />
        </div>

        <div className="relative z-10 mx-auto max-w-370 px-5 pt-6 pb-12 sm:px-7 sm:py-12 md:px-16 md:py-14">
          <CornerFrame />

          <div className="absolute top-10 left-20 z-3 hidden flex-col gap-1 md:flex">
            <span className="font-mono font-semibold text-[10px] text-muted-foreground uppercase tracking-[0.4em]">
              File · 0001 / DIR
            </span>
            <span className="font-mono text-[10px] text-muted-foreground tracking-[0.18em]">
              CLASS · A · ARCHIVE
            </span>
          </div>
          <div className="absolute top-10 right-20 z-3 hidden flex-col gap-1 text-right md:flex">
            <span className="font-mono font-semibold text-[10px] text-muted-foreground uppercase tracking-[0.4em]">
              Subject · PS-2017
            </span>
            <span className="font-mono text-[10px] text-muted-foreground tracking-[0.18em]">
              28°34'N · 83°37'E
            </span>
          </div>

          <div className="relative z-2 w-full min-w-0 max-w-220 pt-2 sm:pt-16 md:pt-24">
            <h1
              className={cn(
                neonHeadingClass,
                "mt-2 mb-6 text-[clamp(2rem,5.6vw,4.8rem)] leading-[1] tracking-[-0.02em] sm:mt-5 sm:mb-8"
              )}
            >
              <span className="block">Pradeep</span>
              <span className="block">Shahi</span>
            </h1>

            <div className="mb-9 flex w-full min-w-0 max-w-full flex-col gap-4 break-words font-mono text-foreground text-sm leading-relaxed sm:max-w-[60ch] sm:text-base sm:leading-[1.7]">
              <p>
                A decade at the lens, this British-Nepali filmmaker has
                delivered two features to Amazon Prime while capturing the soul
                of his homeland.
              </p>
              <p>
                He is now architecting the EKLO-Verse, a mythic sci-fi trilogy
                and Nepal's first cinematic export designed for a global stage.
              </p>
              <p>
                With the first installment complete and the second in
                pre-production, he is building a high-altitude wasteland of
                prophecy, zombies, and decaying hope.
              </p>
            </div>

            <div className="mb-10 grid grid-cols-1 gap-6 sm:mb-14 sm:grid-cols-2 sm:gap-7 sm:gap-x-12">
              <Fact label="Born" value="Dharan, Nepal" />
              <Fact label="Education" value="University of Winchester · UK" />
              <Fact label="Based in" value="London, UK" />
              <Fact label="Studio" value="SHY Productions Ltd" />
            </div>
          </div>

          <div className="relative z-2 -mx-5 mt-6 mb-5 sm:-mx-7 sm:-mt-7 sm:mt-0 md:-mx-16">
            <div className="px-5 sm:px-7 md:px-16">
              <div className="max-w-220">
                <SectionHeader num="FILM·01" label="Filmography" />
              </div>
            </div>
            <div className="flex snap-x snap-mandatory gap-3 overflow-x-auto py-3.5 pr-[12vw] pl-5 [scrollbar-width:none] sm:gap-[18px] sm:px-7 md:pl-16 [&::-webkit-scrollbar]:hidden">
              <FilmCard
                year="2017 · Feature"
                image="https://70fy8xbp6b.ufs.sh/f/BzM3qquY5hLZGKKcOAzmgckfZp8Hjs9wCL3dTYvVoqayXJni"
                title="1915: Legend of the Gurkhas"
                sub="Amazon Prime"
                href="https://www.primevideo.com/detail/0I7HFSILSE59NAD6D51W6CDFQC"
              />
              <FilmCard
                year="2022 · Feature"
                image="https://70fy8xbp6b.ufs.sh/f/BzM3qquY5hLZDlREwzLyt1mqJuWcIYPdGka7oUfCMDnXNrZB"
                title="The Last Ritual"
                sub="Amazon Prime"
                href="https://www.primevideo.com/detail/0FSNB981LE74SNFKWA49303Y7Q"
              />
              <FilmCard
                year="2025 · Trilogy · I"
                image="https://70fy8xbp6b.ufs.sh/f/BzM3qquY5hLZzjjKUCglORy2341i8QXuZ6TqMrnohvEAxafC"
                title="EKLO — I"
                sub="TBC"
                current
              />
              <FilmCard
                year="2026 · Trilogy · II"
                title="EKLO · II"
                sub="Pre-Production"
                locked
              />
            </div>
          </div>

          <div className="relative z-2 w-full min-w-0 max-w-220">
            <SectionHeader num="REC·02" label="Selected recognition" />
            <div className="grid grid-cols-1 gap-x-9 sm:grid-cols-2">
              <div className="flex flex-col">
                <AwardLink href="https://www.imdb.com/name/nm4368555/">
                  IMDB — Pradeep Shahi
                </AwardLink>
                <AwardLink href="https://deadline.com/2024/05/jamie-bacon-pradeep-khadka-uk-nepal-film-eklo-pradeep-shahi-1235920713/">
                  Deadline — UK·Nepal film EKLO announced
                </AwardLink>
              </div>
              <div className="flex flex-col">
                <AwardLink href="https://variety.com/2025/film/news/nepal-sci-fi-film-eklo-jamie-bacon-pradeep-khadka-january-release-1236611608/">
                  Variety — Nepal sci-fi EKLO release
                </AwardLink>
                <AwardLink href="https://gurusmagazine.com/eklo-the-sci-fi-thriller-putting-nepalese-cinema-on-the-global-stage/">
                  Gurus — Nepalese cinema, global stage
                </AwardLink>
              </div>
            </div>

            <div className="mt-10 mb-9 flex flex-col flex-wrap gap-y-6 sm:mt-14 sm:flex-row sm:gap-x-14 sm:gap-y-7">
              <ContactRow label="Director correspondence">
                <a
                  href="https://pradeepshahi.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex break-all font-mono text-foreground text-sm underline decoration-white/25 underline-offset-4 transition-colors hover:text-brand hover:decoration-brand"
                >
                  pradeepshahi.com
                </a>
              </ContactRow>
              <ContactRow label="Studio · SHY Productions Ltd">
                <a
                  href="mailto:contact@shyproductions.co.uk"
                  className="inline-flex break-all font-mono text-foreground text-sm underline decoration-white/25 underline-offset-4 transition-colors hover:text-brand hover:decoration-brand"
                >
                  contact@shyproductions.co.uk · +44 7517 927542
                </a>
              </ContactRow>
            </div>

            <blockquote className="max-w-none border-brand border-l-2 py-2 pl-6 font-mono text-base text-foreground leading-relaxed sm:max-w-[56ch]">
              "EKLO is the first film of a planned trilogy — a fusion of sci-fi,
              spirituality, and survival, rooted in the lore of Kalyug, Satya
              Yuga, and the prophecy of Kalki."
              <cite className="mt-3.5 block font-mono font-semibold text-[10px] text-muted-foreground uppercase not-italic tracking-[0.22em]">
                — Pradeep Shahi · director's vision
              </cite>
            </blockquote>
          </div>
        </div>
      </section>
    </div>
  )
}

function CornerFrame() {
  return (
    <div className="pointer-events-none absolute inset-6 hidden md:block">
      <i className="absolute top-0 left-0 size-7 border-white/25 border-t border-l" />
      <i className="absolute top-0 right-0 size-7 border-white/25 border-t border-r" />
      <i className="absolute bottom-0 left-0 size-7 border-white/25 border-b border-l" />
      <i className="absolute right-0 bottom-0 size-7 border-white/25 border-r border-b" />
    </div>
  )
}

function Fact({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <span className="font-mono text-[9px] text-muted-foreground uppercase tracking-[0.35em]">
        {label}
      </span>
      <div className="font-mono text-[13px] text-foreground uppercase leading-snug tracking-[0.04em]">
        {value}
      </div>
    </div>
  )
}

type FilmCardProps = {
  year: string
  title: string
  sub: string
  image?: string
  href?: string
  current?: boolean
  locked?: boolean
}

function FilmCard({
  year,
  title,
  sub,
  image,
  href,
  current,
  locked,
}: FilmCardProps) {
  const cardClass = cn(
    "flex w-60 flex-shrink-0 snap-start flex-col gap-3 border bg-[#07101f]/60 p-3.5 pb-4 backdrop-blur-sm transition-all duration-200 sm:w-[290px]",
    current && "border-brand bg-brand/[0.05]",
    locked && "cursor-default opacity-65",
    !locked &&
      "cursor-pointer hover:-translate-y-0.5 hover:border-brand hover:bg-brand/[0.06]",
    !current && !locked && "border-white/15"
  )
  const Wrapper = ({ children }: { children: ReactNode }) =>
    href && !locked ? (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={cardClass}
      >
        {children}
      </a>
    ) : (
      <button type="button" className={cardClass}>
        {children}
      </button>
    )
  return (
    <Wrapper>
      <div className="relative aspect-video overflow-hidden border border-white/15 bg-linear-to-br from-[#0c1a2e] to-[#07101f]">
        {image ? (
          <img
            src={image}
            alt={title}
            width={1600}
            height={900}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-3 text-muted-foreground">
            <span className="font-mono text-3xl text-muted-foreground opacity-55">
              ▣
            </span>
          </div>
        )}
        <span
          className={cn(
            "absolute right-3 bottom-3 flex size-9 items-center justify-center rounded-full border bg-white/15 text-white transition-all",
            locked
              ? "border-white/30 bg-transparent text-muted-foreground"
              : "border-white"
          )}
        >
          {locked ? (
            <svg viewBox="0 0 24 24" className="size-3.5 fill-current">
              <title>locked</title>
              <path d="M6 10V8a6 6 0 1 1 12 0v2h1v11H5V10h1zm2 0h8V8a4 4 0 1 0-8 0v2z" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" className="size-3.5 fill-current">
              <title>play</title>
              <path d="M8 5v14l11-7z" />
            </svg>
          )}
        </span>
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-linear-to-b from-transparent via-transparent to-[#030611]/60"
        />
      </div>
      <div className="flex flex-col gap-1 text-left">
        <span
          className={cn(
            "font-mono text-[10px] uppercase tracking-[0.4em]",
            current ? "text-brand" : "text-muted-foreground"
          )}
        >
          {year}
        </span>
        <span className="font-medium font-mono text-base text-foreground uppercase leading-snug tracking-[-0.01em]">
          {title}
        </span>
        <span className="font-mono text-[10px] text-muted-foreground tracking-[0.04em]">
          {sub}
        </span>
      </div>
    </Wrapper>
  )
}

function AwardLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="block py-2.5 text-left font-mono text-foreground text-sm underline decoration-1 decoration-white/25 underline-offset-4 transition-colors hover:text-brand hover:decoration-brand"
    >
      {children}
    </a>
  )
}
